import pb,{currentUser} from './pb.js';
import {getDefaultFaviconUrl,getFaviconDisplayUrl} from './utils/favicon.js';

const activeRequests=new Map();
const clampFolderSortOrder=(value)=> Math.min(10,Math.max(0,Number(value) || 0));

export const showToast=(message,type='info',duration=3500)=> {
  if (typeof document==='undefined') return;
  const notification=document.createElement('div');
  const bgColor=type==='error' ? 'bg-red-600' : type==='warning' ? 'bg-amber-600' : type==='success' ? 'bg-emerald-600' : 'bg-blue-600';
  notification.className=`fixed top-20 right-4 z-[200] max-w-[calc(100vw-2rem)] px-4 py-3 rounded-xl shadow-xl text-white text-sm font-semibold ${bgColor} transition-opacity duration-300`;
  notification.textContent=message;
  document.body.appendChild(notification);
  setTimeout(()=> {
    notification.style.opacity='0';
    setTimeout(()=> notification.remove(),300);
  },duration);
};

export const isOfflineError=(error)=> {
  if (typeof navigator!=='undefined' && navigator.onLine===false) return true;
  const status=error?.status ?? error?.response?.status ?? error?.response?.code;
  if (status===0) return true;
  const message=String(error?.message || error?.response?.message || '').toLowerCase();
  return message.includes('failed to fetch') || message.includes('networkerror') || message.includes('network request failed') || message.includes('load failed') || message.includes('connection');
};

const handleError=(error,context,{silent=false}={})=> {
  console.error(`Failed to ${context}:`,error);
  if (error?.isAbort || error?.code==='ABORT_ERR' || error?.message?.includes('aborted')) return;

  const offline=isOfflineError(error);
  const errorMessage=error?.response?.message || error?.message || `Failed to ${context}`;
  if (!silent && !offline) showToast(`Failed to ${context}: ${errorMessage}`,'error');

  const wrapped=new Error(errorMessage);
  wrapped.isOffline=offline;
  wrapped.status=error?.status ?? error?.response?.status ?? 0;
  wrapped.originalError=error;
  throw wrapped;
};

export const normaliseTags=(value)=> {
  if (Array.isArray(value)) return value.map(tag=> String(tag).trim()).filter(Boolean);
  if (value===null || value===undefined || value==='') return [];
  if (typeof value==='string') {
    const trimmed=value.trim();
    if (!trimmed) return [];
    try {
      const parsed=JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed.map(tag=> String(tag).trim()).filter(Boolean);
    } catch {
      // Legacy records may contain comma-separated text inside the JSON field.
    }
    return trimmed.split(',').map(tag=> tag.trim()).filter(Boolean);
  }
  return [];
};

const safeDate=(value)=> value ? new Date(value) : new Date();

export const mapFolder=(record)=> ({
  id: record.id,
  name: record.name || '',
  parent: record.parent || null,
  sortOrder: Number.isFinite(record.sortOrder) ? record.sortOrder : 0,
  owner: record.owner,
  dateAdded: safeDate(record.created),
  dateModified: safeDate(record.updated || record.created)
});

export const mapBookmark=(record)=> ({
  id: record.id,
  title: record.title || '',
  url: record.url || '',
  description: record.description || '',
  tags: normaliseTags(record.tags),
  folderId: record.folder || null,
  faviconurl: record.faviconurl || null,
  faviconfile: record.faviconfile || null,
  favicon: getFaviconDisplayUrl(record,pb),
  sortOrder: Number.isFinite(record.sortOrder) ? record.sortOrder : 0,
  owner: record.owner,
  dateAdded: safeDate(record.dateAdded || record.created),
  dateModified: safeDate(record.updated || record.created)
});

const requireUser=()=> {
  const user=currentUser();
  if (!user) throw new Error('User not authenticated');
  return user;
};

export const listFolders=async ({silent=false}={})=> {
  try {
    const user=requireUser();
    const records=await pb.collection('folders').getFullList({filter: `owner="${user.id}"`,sort: 'sortOrder,name'});
    return records.map(mapFolder);
  } catch (error) {
    handleError(error,'load folders',{silent});
  }
};

export const getFolder=async (id,{silent=false}={})=> {
  try {
    const user=requireUser();
    const record=await pb.collection('folders').getOne(id);
    if (record.owner!==user.id) throw new Error('You do not have permission to access this folder');
    return mapFolder(record);
  } catch (error) {
    handleError(error,'load folder',{silent});
  }
};

export const createFolder=async (name,parent=null,sortOrder=0,{silent=false}={})=> {
  try {
    const user=requireUser();
    const record=await pb.collection('folders').create({name,parent: parent || '',sortOrder: clampFolderSortOrder(sortOrder),owner: user.id});
    if (!silent) showToast('Folder synced','success');
    return mapFolder(record);
  } catch (error) {
    handleError(error,'create folder',{silent});
  }
};

export const updateFolder=async (id,patch,{silent=false}={})=> {
  try {
    requireUser();
    const data={};
    if (patch.name!==undefined) data.name=patch.name;
    if (patch.parent!==undefined) data.parent=patch.parent || '';
    if (patch.sortOrder!==undefined) data.sortOrder=clampFolderSortOrder(patch.sortOrder);
    const record=await pb.collection('folders').update(id,data);
    if (!silent) showToast('Folder synced','success');
    return mapFolder(record);
  } catch (error) {
    handleError(error,'update folder',{silent});
  }
};

export const deleteFolder=async (id,{silent=false}={})=> {
  try {
    requireUser();
    await pb.collection('folders').delete(id);
    if (!silent) showToast('Folder deleted','success');
    return true;
  } catch (error) {
    handleError(error,'delete folder',{silent});
  }
};

export const bulkDeleteFolders=async (ids,{silent=false}={})=> {
  try {
    requireUser();
    await Promise.all(ids.map(id=> pb.collection('folders').delete(id)));
    if (!silent && ids.length) showToast(`${ids.length} folder${ids.length===1 ? '' : 's'} deleted`,'success');
    return true;
  } catch (error) {
    handleError(error,'bulk delete folders',{silent});
  }
};

export const listBookmarks=async (activeFolderId='ALL',q='',{silent=false}={})=> {
  const requestKey=`listBookmarks_${activeFolderId}_${q}`;
  try {
    const user=requireUser();
    for (const [key,controller] of activeRequests.entries()) {
      if (key.startsWith('listBookmarks_')) {
        controller.abort();
        activeRequests.delete(key);
      }
    }

    const controller=new AbortController();
    activeRequests.set(requestKey,controller);
    const filters=[`owner="${user.id}"`];
    if (activeFolderId==='UNFILED') filters.push('folder=null');
    else if (activeFolderId && activeFolderId!=='ALL') filters.push(`folder="${activeFolderId}"`);
    if (q) filters.push(`(title ~ "${q}" || description ~ "${q}" || url ~ "${q}" || tags ~ "${q}")`);

    const records=await pb.collection('bookmarks').getFullList({filter: filters.join(' && '),sort: 'sortOrder,created',signal: controller.signal});
    activeRequests.delete(requestKey);
    return records.map(mapBookmark);
  } catch (error) {
    activeRequests.delete(requestKey);
    handleError(error,'load bookmarks',{silent});
  }
};

export const getAllBookmarks=async ({silent=false}={})=> {
  try {
    const user=requireUser();
    const records=await pb.collection('bookmarks').getFullList({filter: `owner="${user.id}"`,sort: 'sortOrder,created'});
    return records.map(mapBookmark);
  } catch (error) {
    handleError(error,'load all bookmarks',{silent});
  }
};

export const getBookmarkCounts=async ({silent=false}={})=> {
  try {
    const user=requireUser();
    const uid=user.id;
    const [allResult,unfiledResult]=await Promise.all([
      pb.collection('bookmarks').getList(1,1,{filter: `owner="${uid}"`}),
      pb.collection('bookmarks').getList(1,1,{filter: `owner="${uid}" && folder=null`})
    ]);
    return {all: allResult.totalItems,unfiled: unfiledResult.totalItems};
  } catch (error) {
    handleError(error,'load bookmark counts',{silent});
  }
};

export const getFolderBookmarkCount=async (folderId,{silent=false}={})=> {
  try {
    const user=requireUser();
    const result=await pb.collection('bookmarks').getList(1,1,{filter: `owner="${user.id}" && folder="${folderId}"`});
    return result.totalItems;
  } catch (error) {
    handleError(error,'load folder bookmark count',{silent});
  }
};

export const createBookmark=async (bookmark,{silent=false}={})=> {
  try {
    const user=requireUser();
    const formData=new FormData();
    formData.append('title',bookmark.title || '');
    formData.append('url',bookmark.url || '');
    formData.append('description',bookmark.description || '');
    formData.append('tags',JSON.stringify(normaliseTags(bookmark.tags)));
    if (bookmark.folderId && bookmark.folderId!=='UNFILED' && bookmark.folderId!=='ALL') formData.append('folder',bookmark.folderId);
    formData.append('sortOrder',bookmark.sortOrder || 0);
    formData.append('owner',user.id);
    const faviconUrl=bookmark.faviconurl || getDefaultFaviconUrl(bookmark.url);
    if (faviconUrl) formData.append('faviconurl',faviconUrl);
    if (bookmark.faviconfile) formData.append('faviconfile',bookmark.faviconfile);

    const record=await pb.collection('bookmarks').create(formData);
    if (!silent) showToast('Bookmark synced','success');
    return mapBookmark(record);
  } catch (error) {
    handleError(error,'create bookmark',{silent});
  }
};

export const updateBookmark=async (id,patch,{silent=false}={})=> {
  try {
    requireUser();
    let currentRecord=null;
    if (patch.url!==undefined && patch.faviconurl===undefined) {
      currentRecord=await pb.collection('bookmarks').getOne(id);
    }

    const formData=new FormData();
    if (patch.title!==undefined) formData.append('title',patch.title);
    if (patch.url!==undefined) formData.append('url',patch.url);
    if (patch.description!==undefined) formData.append('description',patch.description);
    if (patch.tags!==undefined) formData.append('tags',JSON.stringify(normaliseTags(patch.tags)));
    if (patch.folderId!==undefined) {
      if (patch.folderId && patch.folderId!=='UNFILED' && patch.folderId!=='ALL') formData.append('folder',patch.folderId);
      else formData.append('folder','');
    }
    if (patch.sortOrder!==undefined) formData.append('sortOrder',patch.sortOrder);
    if (patch.faviconurl!==undefined) formData.append('faviconurl',patch.faviconurl || '');
    else if (patch.url && currentRecord && patch.url!==currentRecord.url && !currentRecord.faviconfile) {
      const nextFavicon=getDefaultFaviconUrl(patch.url);
      if (nextFavicon) formData.append('faviconurl',nextFavicon);
    }
    if (patch.faviconfile!==undefined) formData.append('faviconfile',patch.faviconfile===null ? '' : patch.faviconfile);

    const record=await pb.collection('bookmarks').update(id,formData);
    if (!silent) showToast('Bookmark synced','success');
    return mapBookmark(record);
  } catch (error) {
    handleError(error,'update bookmark',{silent});
  }
};

export const deleteBookmark=async (id,{silent=false}={})=> {
  try {
    requireUser();
    await pb.collection('bookmarks').delete(id);
    if (!silent) showToast('Bookmark deleted','success');
    return true;
  } catch (error) {
    handleError(error,'delete bookmark',{silent});
  }
};

export const bulkDeleteBookmarks=async (ids,{silent=false}={})=> {
  try {
    requireUser();
    await Promise.all(ids.map(id=> pb.collection('bookmarks').delete(id)));
    if (!silent && ids.length) showToast(`${ids.length} bookmark${ids.length===1 ? '' : 's'} deleted`,'success');
    return true;
  } catch (error) {
    handleError(error,'bulk delete bookmarks',{silent});
  }
};

export const getSetting=async (key,{silent=true}={})=> {
  try {
    const user=requireUser();
    const records=await pb.collection('settings').getFullList({filter: `owner="${user.id}" && key="${key}"`});
    if (records.length===0) return null;
    const value=records[0].value;
    if (typeof value!=='string') return value;
    try {return JSON.parse(value);} catch {return value;}
  } catch (error) {
    if (!silent) handleError(error,'load setting',{silent});
    console.error('Failed to get setting:',error);
    if (isOfflineError(error)) {
      const wrapped=new Error(error.message || 'Unable to load setting');
      wrapped.isOffline=true;
      throw wrapped;
    }
    return null;
  }
};

export const setSetting=async (key,value,{silent=false}={})=> {
  try {
    const user=requireUser();
    const existing=await pb.collection('settings').getFullList({filter: `owner="${user.id}" && key="${key}"`});
    if (existing.length>0) await pb.collection('settings').update(existing[0].id,{value});
    else await pb.collection('settings').create({key,value,owner: user.id});
    if (!silent) showToast('Settings synced','success');
    return true;
  } catch (error) {
    handleError(error,'save setting',{silent});
  }
};

export const moveBookmarksToRoot=async (folderIds,{silent=false}={})=> {
  try {
    const user=requireUser();
    if (!folderIds.length) return 0;
    const folderFilter=folderIds.map(id=> `folder="${id}"`).join(' || ');
    const bookmarks=await pb.collection('bookmarks').getFullList({filter: `owner="${user.id}" && (${folderFilter})`});
    await Promise.all(bookmarks.map(bookmark=> pb.collection('bookmarks').update(bookmark.id,{folder: ''})));
    if (!silent && bookmarks.length) showToast(`${bookmarks.length} bookmark${bookmarks.length===1 ? '' : 's'} moved to Unfiled`,'success');
    return bookmarks.length;
  } catch (error) {
    handleError(error,'move bookmarks',{silent});
  }
};

export const subscribeToLibrary=async ({onBookmark,onFolder}={})=> {
  const user=requireUser();
  const ownerId=user.id;
  const bookmarkUnsubscribe=await pb.collection('bookmarks').subscribe('*',(event)=> {
    const record=event?.record;
    if (!record || (record.owner && record.owner!==ownerId)) return;
    onBookmark?.({action: event.action,record: mapBookmark(record)});
  });
  const folderUnsubscribe=await pb.collection('folders').subscribe('*',(event)=> {
    const record=event?.record;
    if (!record || (record.owner && record.owner!==ownerId)) return;
    onFolder?.({action: event.action,record: mapFolder(record)});
  });

  return async ()=> {
    try {await bookmarkUnsubscribe?.();} catch {}
    try {await folderUnsubscribe?.();} catch {}
  };
};